// src/redux/api/portalApi.js
import { TBD_FINDER_PAUSED } from '../../config/tbdFinder';
// Central RTK Query API service for the Production Portal Frontend.
// Uses a custom axiosBaseQuery so the existing retry / progressive-timeout
// logic in axiosConfig.js is reused for every RTK Query endpoint.

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { createApi } from '@reduxjs/toolkit/query/react';
import axiosInstance from '../../axios/axiosConfig';
import { createBackgroundRequestQueue } from './backgroundRequests';
import { abortableDelay } from '../../axios/abortableDelay';
import { fetchShippingSchedulePages, SHIPPING_SCHEDULE_CONTRACT } from './shippingSchedulePagination';
import {
  createCorrelationId,
  getRetryDelayMs,
  normalizeApiError,
  normalizeRequestData,
  normalizeResponseData,
  shouldRetryRequest,
} from './contracts';
import {
  buildGeneralProjectPageUrl,
  fetchAllGeneralProjectPages,
  GENERAL_PROJECT_LIST_CONTRACT,
} from './generalProjectPagination';

const getEndpointNameFromUrl = (rawUrl = '') => {
  try {
    const parsed = new URL(String(rawUrl), 'http://localhost');
    return parsed.pathname
      .replace(/\/+$/, '')
      .split('/')
      .filter(Boolean)
      .pop()
      ?.toLowerCase() || '';
  } catch (_) {
    const cleanPath = String(rawUrl || '').split('?')[0];
    return cleanPath
      .replace(/\/+$/, '')
      .split('/')
      .filter(Boolean)
      .pop()
      ?.toLowerCase() || '';
  }
};

const parseUrlDetails = (rawUrl = '', fallbackBase = '') => {
  try {
    const parsed = new URL(String(rawUrl || ''), String(fallbackBase || 'http://localhost'));
    return {
      isAbsolute: /^https?:\/\//i.test(String(rawUrl || '').trim()),
      hostname: parsed.hostname.toLowerCase(),
      pathname: (parsed.pathname || '').toLowerCase(),
    };
  } catch (_) {
    const raw = String(rawUrl || '').trim().toLowerCase();
    return {
      isAbsolute: /^https?:\/\//i.test(raw),
      hostname: '',
      pathname: raw.split('?')[0],
    };
  }
};

const shouldBypassAutoRequestCase = (rawUrl = '', baseUrl = '') => {
  const requestDetails = parseUrlDetails(rawUrl, baseUrl);
  const baseDetails = parseUrlDetails(baseUrl, 'http://localhost');

  const isGraphEndpoint = requestDetails.hostname.includes('graph.microsoft.com');
  const isSharePointRestEndpoint = requestDetails.pathname.includes('/_api/');

  if (isGraphEndpoint || isSharePointRestEndpoint) {
    return true;
  }

  if (
    requestDetails.isAbsolute &&
    baseDetails.hostname &&
    requestDetails.hostname &&
    requestDetails.hostname !== baseDetails.hostname
  ) {
    return true;
  }

  return false;
};

// ---------------------------------------------------------------------------
// Custom base query – wraps the project's axios instance so that all the
// retry / timeout / progress-clearing interceptors defined in axiosConfig.js
// continue to work for every RTK Query endpoint.
// ---------------------------------------------------------------------------
const axiosBaseQuery =
  ({ baseUrl } = { baseUrl: '' }) =>
  async ({ url, method = 'GET', data, params, headers, contract, requestPolicy }, api) => {
    const stateToken = api?.getState?.()?.user?.token;
    const correlationId = headers?.['x-correlation-id'] || createCorrelationId();
    const methodUpper = String(method || 'GET').toUpperCase();
    const maxAttempts = 3;
    const endpointName = getEndpointNameFromUrl(url);
    if (TBD_FINDER_PAUSED && endpointName === 'tbd-finder') {
      return { error: { status: 'PAUSED', message: 'TBD finder requests are temporarily paused.' } };
    }
    const isGeneralProjectEndpoint = endpointName === 'general-project-info';
    const bypassAutoRequestCase = shouldBypassAutoRequestCase(url, baseUrl);

    const resolvedContract = {
      ...(methodUpper === 'POST' ? { requestCase: 'pascal' } : {}),
      ...(methodUpper === 'PATCH' ? { requestCase: 'pascal' } : {}),
      ...(contract || {}),
    };

    if (
      methodUpper === 'POST' &&
      bypassAutoRequestCase &&
      !Object.prototype.hasOwnProperty.call(contract || {}, 'requestCase')
    ) {
      resolvedContract.requestCase = 'raw';
    }

    if (
      isGeneralProjectEndpoint &&
      (methodUpper === 'POST' || methodUpper === 'PUT' || methodUpper === 'PATCH')
    ) {
      resolvedContract.requestCase = 'pascal';
    }

    const sanitizedHeaders = { ...(headers || {}) };

    let attempt = 0;

    while (attempt < maxAttempts) {
      attempt += 1;

      try {
      const requestUrl = (() => {
        const rawUrl = String(url ?? '');
        if (/^https?:\/\//i.test(rawUrl)) {
          return rawUrl;
        }

        if (!baseUrl) {
          return rawUrl;
        }

        const normalizedBase = String(baseUrl).replace(/\/+$/, '');
        const normalizedPath = rawUrl.replace(/^\/+/, '');
        return `${normalizedBase}/${normalizedPath}`;
      })();

      const result = await axiosInstance({
        url: requestUrl,
        method: methodUpper,
        data: normalizeRequestData({
          data,
          method: methodUpper,
          contract: resolvedContract,
        }),
        params,
        headers: {
          ...sanitizedHeaders,
          ...(stateToken ? { Authorization: `Bearer ${stateToken}` } : {}),
          'x-client-source': `portalApi:${api?.endpoint || 'unknown'}`,
          'x-correlation-id': correlationId,
        },
        // Pass RTK Query's cancellation signal so that component unmounts /
        // query argument changes cancel the in-flight axios request.
        // axiosConfig.js forwards this to its own progressive-timeout
        // AbortController, so both RTK Query and the timeout can cancel.
        signal: api?.signal,
        _retryEnabled: requestPolicy?.retry !== false,
        _requestTimeoutMs: requestPolicy?.timeoutMs,
      });

      return {
        data: normalizeResponseData(result.data, resolvedContract?.response || {}),
        meta: { correlationId },
      };
      } catch (axiosError) {
        const status = axiosError?.response?.status ?? null;
        const axiosRetryCount = Number(axiosError?.config?.retryCount || 0);
        const alreadyRetriedByAxios = axiosRetryCount > 0;

        const canRetry = requestPolicy?.retry !== false && !api?.signal?.aborted && !alreadyRetriedByAxios &&
          shouldRetryRequest({
            method: methodUpper,
            status,
            error: axiosError,
            attempt,
            maxAttempts,
          });

        if (canRetry) {
          const waitMs = getRetryDelayMs(attempt);
          try {
            await abortableDelay(waitMs, api?.signal);
            continue;
          } catch (error) {
            return { error: normalizeApiError({ error, url, method: methodUpper, correlationId }) };
          }
        }

        return {
          error: normalizeApiError({
            error: axiosError,
            url,
            method: methodUpper,
            correlationId,
          }),
          meta: { correlationId },
        };
      }
    }

    return {
      error: {
        status: null,
        message: 'Request failed after retries',
        isNetworkError: true,
        isTimeout: false,
        isCanceled: false,
        endpoint: url,
        method: methodUpper,
        correlationId,
      },
      meta: { correlationId },
    };
  };

export const fullScheduleRequest = url => {
  const endpoint = normalizeEndpointRoot(url, '/full-schedule');
  return { url: `${endpoint}${endpoint.includes('?') ? '&' : '?'}page=1&projectStatusNotEqual=Shipped`, contract: {} };
};

const backgroundRequests = createBackgroundRequestQueue(2,
  () => typeof document === 'undefined' || document.visibilityState !== 'hidden');
export const resumeBackgroundRequests = () => backgroundRequests.resume();
const backgroundBaseQuery = axiosBaseQuery({ baseUrl: process.env.REACT_APP_API_URL || '' });
const RAW_BACKGROUND_CONTRACT = { response: { envelope: [], keepOriginalKeys: true, includeMappedKeys: false } };
let backgroundVisibilityListener = false;

// Background snapshots use the same transport, auth and error handling as RTK
// queries, but do not populate editable caches or mark foreground queries busy.
// The coordinator owns retries; each read gets one generous attempt.
export const fetchBackgroundData = ({ signal, contract = RAW_BACKGROUND_CONTRACT, ...args }) =>
  (_dispatch, getState) => {
    if (!backgroundVisibilityListener && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', resumeBackgroundRequests);
      backgroundVisibilityListener = true;
    }
    const state = getState();
    const identity = state.user?.userDetails?.localAccountId || state.user?.userDetails?.username;
    const key = JSON.stringify([identity, state.user?.token, args, contract]);
    return backgroundRequests.run(key, async sharedSignal => {
      const result = await backgroundBaseQuery({ ...args, method: 'GET', contract,
        requestPolicy: { retry: false, timeoutMs: 45000 } },
      { getState, signal: sharedSignal, endpoint: 'backgroundSnapshot' });
      if (result.error) throw result.error;
      return result.data;
    }, signal);
  };

// Read-only query consumers can opt into background policy without changing
// their cache keys. Never overwrite a newer foreground request or a draft.
export const refreshBackgroundQuery = (endpoint, args, { signal, canApply = () => true } = {}) =>
  async (dispatch, getState) => {
    if (!['getData', 'getShippingSchedule'].includes(endpoint)) throw new Error('Unsupported background query');
    const select = () => portalApi.endpoints[endpoint].select(args)(getState());
    const before = select();
    if (before.status === 'pending' || !canApply()) return false;
    try {
      const result = endpoint === 'getShippingSchedule'
        ? await fetchShippingSchedulePages(async page => ({ data: await dispatch(fetchBackgroundData({
          url: args.url, params: { ...args.params, page }, contract: SHIPPING_SCHEDULE_CONTRACT, signal,
        })) }))
        : { data: await dispatch(fetchBackgroundData({ ...args, contract: args.contract || {}, signal })) };
      const after = select();
      if (result.error || signal?.aborted || !canApply() || after.status === 'pending' || after.requestId !== before.requestId) return false;
      if (after.status === 'rejected' || after.data === undefined) {
        await dispatch(portalApi.util.upsertQueryData(endpoint, args, result.data));
      } else {
        dispatch(portalApi.util.updateQueryData(endpoint, args, () => result.data));
      }
      return true;
    } catch { return false; }
  };

const PATCH_ID_KEYS = new Set(['ID', 'id', 'ProjectID', 'projectID']);

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === '[object Object]';

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return value.toISOString().split('T')[0];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed === '') {
      return null;
    }

    if (trimmed === 'true') {
      return true;
    }

    if (trimmed === 'false') {
      return false;
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const parsed = Date.parse(trimmed);
      if (!Number.isNaN(parsed)) {
        return new Date(parsed).toISOString().split('T')[0];
      }
    }

    return trimmed;
  }

  if (typeof value === 'number' && Number.isNaN(value)) {
    return null;
  }

  if (Array.isArray(value) || isPlainObject(value)) {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return value;
    }
  }

  return value;
};

const valuesAreEquivalent = (left, right) =>
  normalizeComparableValue(left) === normalizeComparableValue(right);

const sanitizePatchPayload = (payload) => {
  if (!isPlainObject(payload)) {
    return payload;
  }

  const sanitized = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (typeof key === 'string' && (key.startsWith('_') || key.includes('_showAsText'))) {
      return;
    }

    sanitized[key] = value;
  });

  return sanitized;
};

const parseUrlContext = (rawUrl = '') => {
  try {
    const parsed = new URL(String(rawUrl), 'http://localhost');
    const endpoint = parsed.pathname
      .replace(/\/+$/, '')
      .split('/')
      .filter(Boolean)
      .pop()
      ?.toLowerCase() || '';

    const idFromQuery =
      parsed.searchParams.get('id') ||
      parsed.searchParams.get('ID') ||
      parsed.searchParams.get('projectID') ||
      parsed.searchParams.get('projectId') ||
      null;

    return { endpoint, idFromQuery };
  } catch (_) {
    const clean = String(rawUrl || '');
    const [pathPart, queryPart = ''] = clean.split('?');
    const endpoint = pathPart
      .replace(/\/+$/, '')
      .split('/')
      .filter(Boolean)
      .pop()
      ?.toLowerCase() || '';

    const params = new URLSearchParams(queryPart);
    const idFromQuery =
      params.get('id') ||
      params.get('ID') ||
      params.get('projectID') ||
      params.get('projectId') ||
      null;

    return { endpoint, idFromQuery };
  }
};

const resolveGeneralProjectBaseline = (state, recordId) => {
  if (!state || recordId === null || recordId === undefined) {
    return null;
  }

  const idAsString = String(recordId);

  const scheduleData = state?.jobSchedule?.data;
  if (Array.isArray(scheduleData)) {
    const scheduleMatch = scheduleData.find(
      (item) => String(item?.ID ?? item?.ProjectID ?? '') === idAsString
    );
    if (scheduleMatch) {
      return scheduleMatch;
    }
  }

  const generalProject = state?.jobDetails?.jobDetails?.generalProject;
  if (Array.isArray(generalProject)) {
    const detailsMatch = generalProject.find(
      (item) => String(item?.ID ?? item?.ProjectID ?? '') === idAsString
    );
    if (detailsMatch) {
      return detailsMatch;
    }
  }

  if (isPlainObject(generalProject)) {
    const detailsId = String(generalProject?.ID ?? generalProject?.ProjectID ?? '');
    if (detailsId === idAsString) {
      return generalProject;
    }
  }

  return null;
};

const derivePatchBaselineFromState = ({ state, url, payload }) => {
  const { endpoint, idFromQuery } = parseUrlContext(url);

  const payloadId =
    payload?.ID ??
    payload?.id ??
    payload?.ProjectID ??
    payload?.projectID ??
    null;

  const recordId = idFromQuery ?? payloadId;

  if (endpoint === 'general-project-info') {
    return resolveGeneralProjectBaseline(state, recordId);
  }

  return null;
};

const buildPatchDelta = ({ payload, baseline }) => {
  if (!isPlainObject(payload) || !isPlainObject(baseline)) {
    return payload;
  }

  const delta = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (PATCH_ID_KEYS.has(key)) {
      delta[key] = value;
      return;
    }

    if (!valuesAreEquivalent(value, baseline[key])) {
      delta[key] = value;
    }
  });

  return delta;
};

const normalizeEndpointRoot = (rawUrl, fallbackPath) => {
  const fallback = String(fallbackPath || '').trim() || '/';

  if (typeof rawUrl !== 'string') {
    return fallback;
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return fallback;
  }

  return trimmed.replace(/\/+$/, '') || fallback;
};

const parseDropdownMutationArgs = (args) => {
  const isWrapped = isPlainObject(args) && (
    Object.prototype.hasOwnProperty.call(args, 'data') ||
    Object.prototype.hasOwnProperty.call(args, 'url') ||
    Object.prototype.hasOwnProperty.call(args, 'id')
  );

  const data = isWrapped ? args?.data : args;
  const endpointRoot = normalizeEndpointRoot(
    isWrapped ? args?.url : undefined,
    '/dropdown-values'
  );
  const idFromArgs = isWrapped ? args?.id : undefined;
  const idFromPayload = data?.ID ?? data?.id;
  const id = idFromArgs ?? idFromPayload ?? null;

  return { endpointRoot, data, id };
};

const shouldFallbackDropdownRoute = (error) => {
  const status = Number(error?.status);
  return status === 404 || status === 405 || status === 501;
};

const buildIdPath = (endpointRoot, id) => {
  if (id === null || id === undefined) {
    return endpointRoot;
  }

  return `${endpointRoot}/${encodeURIComponent(String(id))}`;
};

const buildIdQuery = (endpointRoot, id) => {
  if (id === null || id === undefined) {
    return endpointRoot;
  }

  const separator = endpointRoot.includes('?') ? '&' : '?';
  return `${endpointRoot}${separator}id=${encodeURIComponent(String(id))}`;
};

const normalizePermissionValue = (value, fallback = 'hidden') => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'edit' || normalized === 'view' || normalized === 'hidden') {
    return normalized;
  }

  return fallback;
};

const normalizeBooleanValue = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;

  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') return true;
  if (normalized === 'false' || normalized === '0') return false;

  return fallback;
};

const normalizeEnvironmentValue = (value, fallback = 'production') => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'production' || normalized === 'uat' || normalized === 'dev') {
    return normalized;
  }

  if (normalized === 'development') {
    return 'dev';
  }

  return fallback;
};

const DEFAULT_ENVIRONMENTS = ['dev', 'uat', 'production'];

const normalizeEnvironmentList = (input, fallback = DEFAULT_ENVIRONMENTS) => {
  if (Array.isArray(input)) {
    const values = input
      .map((value) => normalizeEnvironmentValue(value, ''))
      .filter(Boolean);

    return values.length > 0 ? Array.from(new Set(values)) : [...fallback];
  }

  if (typeof input === 'string' && input.trim()) {
    const trimmed = input.trim();

    try {
      const parsed = JSON.parse(trimmed);
      return normalizeEnvironmentList(parsed, fallback);
    } catch (_) {
      const splitValues = trimmed
        .split(',')
        .map((value) => normalizeEnvironmentValue(value, ''))
        .filter(Boolean);

      return splitValues.length > 0 ? Array.from(new Set(splitValues)) : [...fallback];
    }
  }

  const singleValue = normalizeEnvironmentValue(input, '');
  if (singleValue) {
    return [singleValue];
  }

  return [...fallback];
};

const parseRolePermissions = (input) => {
  if (Array.isArray(input)) {
    return input
      .map((entry) => {
        const roleName = entry?.roleName || entry?.RoleName || '';
        const permission = normalizePermissionValue(
          entry?.permission ?? entry?.Permission,
          null
        );

        return {
          roleName,
          permission,
        };
      })
      .filter((entry) => entry.roleName && entry.permission !== null);
  }

  if (typeof input === 'string' && input.trim()) {
    try {
      const parsed = JSON.parse(input);
      return parseRolePermissions(parsed);
    } catch (_) {
      return [];
    }
  }

  return [];
};

const normalizeComponentPolicyRecord = (record) => {
  if (!isPlainObject(record)) {
    return record;
  }

  const rolePermissions = parseRolePermissions(record.rolePermissions ?? record.RolePermissions);
  const environments = normalizeEnvironmentList(
    record.environments ??
    record.Environments ??
    record.environment ??
    record.Environment
  );
  const environment = environments[0] || 'production';
  const defaultPermission = normalizePermissionValue(
    record.defaultPermission ?? record.DefaultPermission,
    null
  );
  const id = record.id ?? record.ID ?? null;
  const policyId = record.policyId ?? record.PolicyId ?? '';
  const componentKey = record.componentKey ?? record.ComponentKey ?? '';
  const enabled = normalizeBooleanValue(record.enabled ?? record.Enabled, true);
  const requiresAuth = normalizeBooleanValue(
    record.requiresAuth ?? record.RequiresAuth,
    false
  );
  const status = String(record.status ?? record.Status ?? '').trim().toLowerCase();
  const rawVersion = record.version ?? record.Version;
  const version = Number.isFinite(Number(rawVersion)) ? Number(rawVersion) : 0;

  return {
    ...record,
    id,
    ID: id,
    policyId,
    PolicyId: policyId,
    componentKey,
    ComponentKey: componentKey,
    enabled,
    Enabled: enabled,
    requiresAuth,
    RequiresAuth: requiresAuth,
    rolePermissions,
    RolePermissions: rolePermissions,
    environments,
    Environments: environments,
    environment,
    Environment: environment,
    defaultPermission,
    DefaultPermission: defaultPermission,
    status,
    Status: status,
    version,
    Version: version,
  };
};

export const normalizeComponentPolicyResponse = (response) => {
  if (Array.isArray(response)) {
    return response.map(normalizeComponentPolicyRecord);
  }

  if (isPlainObject(response)) {
    return normalizeComponentPolicyRecord(response);
  }

  return response;
};

// ---------------------------------------------------------------------------
// RTK Query API definition
// ---------------------------------------------------------------------------
export const portalApi = createApi({
  reducerPath: 'portalApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL || '',
  }),
  refetchOnFocus: false,
  refetchOnReconnect: false,
  // Only refetch on mount/arg change when cache is older than 15 minutes.
  refetchOnMountOrArgChange: 60 * 15,
  // Default: keep cached data for 1 hour.  Individual endpoints can override.
  keepUnusedDataFor: 60 * 60,
  tagTypes: [
    'CriticalData',
    'DropdownValues',
    'TbdData',
    'FullJob',
    'GeneralProjectInfo',
    'SalesTargets',
    'ChangeOrderDetails',
    'ChangeOrderParent',
    'ChangeLog',
    'BookingsLog',
    'JobSchedule',
    'PumpDetails',
    'SoldToShipTo',
    'PriceBreakdown',
    'StationEnclosure',
    'Fertigation',
    'HcRequirements',
    'PumpManifoldPiping',
    'EngineeringTasks',
    'EngineeringTaskComments',
    'ProtectedTime',
    'Pto',
    'UserPreferences',
    'PanelSizing',
    'ComponentPolicies',
  ],
  endpoints: (builder) => ({
    getNpSnapshot: builder.query({
      query: ({ url }) => ({ url }),
      keepUnusedDataFor: 30,
    }),
    // ── Critical Data ───────────────────────────────────────────────────────
    // Replaces: fetchAllCriticalData / refreshCriticalData in criticalDataSlice
    getCriticalData: builder.query({
      query: ({ url = '/critical-data' } = {}) => ({ url }),
      providesTags: ['CriticalData'],
      keepUnusedDataFor: 0, // App holds the session-scoped critical-data subscription.
    }),

    // ── Dropdown Values ─────────────────────────────────────────────────────
    getDropdownValues: builder.query({
      query: (url) => ({
        url: normalizeEndpointRoot(url, '/dropdown-values'),
      }),
      providesTags: (result) => {
        const listTag = { type: 'DropdownValues', id: 'LIST' };

        if (!Array.isArray(result)) {
          return [listTag];
        }

        const entityTags = result
          .map((item) => item?.ID ?? item?.id)
          .filter((id) => id !== undefined && id !== null)
          .map((id) => ({ type: 'DropdownValues', id: String(id) }));

        return [listTag, ...entityTags];
      },
    }),

    createDropdownValue: builder.mutation({
      query: (args) => {
        const { endpointRoot, data } = parseDropdownMutationArgs(args);

        return {
          url: endpointRoot,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: [{ type: 'DropdownValues', id: 'LIST' }],
    }),

    updateDropdownValue: builder.mutation({
      async queryFn(args, api, extraOptions, baseQuery) {
        const { endpointRoot, data, id } = parseDropdownMutationArgs(args);

        const putByRoot = await baseQuery(
          {
            url: endpointRoot,
            method: 'PUT',
            data,
          },
          api,
          extraOptions
        );

        if (!putByRoot?.error) {
          return putByRoot;
        }

        if (!shouldFallbackDropdownRoute(putByRoot.error)) {
          return putByRoot;
        }

        const patchByRoot = await baseQuery(
          {
            url: endpointRoot,
            method: 'PATCH',
            data,
          },
          api,
          extraOptions
        );

        if (!patchByRoot?.error) {
          return patchByRoot;
        }

        if (!shouldFallbackDropdownRoute(patchByRoot.error)) {
          return patchByRoot;
        }

        const putByQuery = await baseQuery(
          {
            url: buildIdQuery(endpointRoot, id),
            method: 'PUT',
            data,
          },
          api,
          extraOptions
        );

        if (!putByQuery?.error) {
          return putByQuery;
        }

        if (!shouldFallbackDropdownRoute(putByQuery.error)) {
          return putByQuery;
        }

        const patchByQuery = await baseQuery(
          {
            url: buildIdQuery(endpointRoot, id),
            method: 'PATCH',
            data,
          },
          api,
          extraOptions
        );

        if (!patchByQuery?.error) {
          return patchByQuery;
        }

        if (!shouldFallbackDropdownRoute(patchByQuery.error)) {
          return patchByQuery;
        }

        return baseQuery(
          {
            url: buildIdPath(endpointRoot, id),
            method: 'PUT',
            data,
          },
          api,
          extraOptions
        );
      },
      invalidatesTags: (result, error, args) => {
        const { id } = parseDropdownMutationArgs(args);
        const recordId = id ?? null;

        return [
          { type: 'DropdownValues', id: 'LIST' },
          ...(recordId !== null && recordId !== undefined
            ? [{ type: 'DropdownValues', id: String(recordId) }]
            : []),
        ];
      },
    }),

    deleteDropdownValue: builder.mutation({
      async queryFn(args, api, extraOptions, baseQuery) {
        const { endpointRoot, id } = parseDropdownMutationArgs(args);

        const deleteByPath = await baseQuery(
          {
            url: buildIdPath(endpointRoot, id),
            method: 'DELETE',
          },
          api,
          extraOptions
        );

        if (!deleteByPath?.error) {
          return deleteByPath;
        }

        if (!shouldFallbackDropdownRoute(deleteByPath.error)) {
          return deleteByPath;
        }

        const deleteByBody = await baseQuery(
          {
            url: endpointRoot,
            method: 'DELETE',
            data: id !== null && id !== undefined ? { ID: id } : undefined,
          },
          api,
          extraOptions
        );

        if (!deleteByBody?.error) {
          return deleteByBody;
        }

        if (!shouldFallbackDropdownRoute(deleteByBody.error)) {
          return deleteByBody;
        }

        return baseQuery(
          {
            url: buildIdQuery(endpointRoot, id),
            method: 'DELETE',
          },
          api,
          extraOptions
        );
      },
      invalidatesTags: (result, error, args) => {
        const { id } = parseDropdownMutationArgs(args);

        return [
          { type: 'DropdownValues', id: 'LIST' },
          ...(id !== null && id !== undefined
            ? [{ type: 'DropdownValues', id: String(id) }]
            : []),
        ];
      },
    }),

    // ── TBD Data ────────────────────────────────────────────────────────────
    // Replaces: fetchTbdData / refreshTbdData in tbdApiSlice
    getTbdData: builder.query({
      query: (jobId) => ({
        url: jobId ? `/tbd-finder?projectID=${jobId}` : '/tbd-finder',
      }),
      providesTags: ['TbdData'],
    }),

    // ── Full Job ────────────────────────────────────────────────────────────
    // Replaces: fetchFullJob / syncJobData in fullJobFetchSlice
    getFullJob: builder.query({
      query: (args) => ({
        url: `${typeof args === 'object' ? args.url : '/full-job'}?id=${encodeURIComponent(typeof args === 'object' ? args.jobId : args)}`,
        contract: {
          response: {
            keepOriginalKeys: true,
            includeMappedKeys: false,
          },
        },
      }),
      providesTags: (result, error, args) => [{ type: 'FullJob', id: typeof args === 'object' ? args.jobId : args }],
    }),

    // ── General Project Info ────────────────────────────────────────────────
    // Replaces: GET calls in checkoutSlice / jobScheduleSlice / zoneCountSlice
    getGeneralProjectInfo: builder.query({
      query: (jobId) => ({ url: `/general-project-info?id=${jobId}` }),
      providesTags: (result, error, jobId) => [
        { type: 'GeneralProjectInfo', id: String(jobId) },
      ],
    }),

    // Replaces: checkoutJob / checkInJob PATCH calls in checkoutSlice
    patchGeneralProjectInfo: builder.mutation({
      query: ({ jobId, data, url }) => ({
        url: url || `/general-project-info?id=${encodeURIComponent(jobId)}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'GeneralProjectInfo', id: String(jobId) },
        { type: 'GeneralProjectInfo', id: 'LIST' },
      ],
    }),

    // ── Job Schedule (list of all jobs) ────────────────────────────────────
    // Replaces: fetchJobSchedule in jobScheduleSlice
    getJobList: builder.query({
      async queryFn(params, _queryApi, _extraOptions, baseQuery) {
        const fetchPage = (page) => baseQuery({
          url: buildGeneralProjectPageUrl(params, page),
          contract: GENERAL_PROJECT_LIST_CONTRACT,
        });

        const result = await fetchAllGeneralProjectPages(fetchPage);
        if (result.error) return { error: result.error };
        return { data: result.records };
      },
      providesTags: (result) => {
        const listTag = { type: 'GeneralProjectInfo', id: 'LIST' };
        if (!Array.isArray(result)) {
          return [listTag, 'JobSchedule'];
        }

        const entityTags = result
          .map((item) => item?.id ?? item?.ID)
          .filter((id) => id !== undefined && id !== null)
          .map((id) => ({ type: 'GeneralProjectInfo', id: String(id) }));

        return [listTag, ...entityTags, 'JobSchedule'];
      },
    }),

    // Full schedule aggregate used by the primary and Assembly schedules.
    // Keep this separate from getJobList because it also returns related
    // TBD, pump, sold-to/ship-to, and change-order records per project.
    getShippingSchedule: builder.query({
      queryFn: ({ url, params }, _api, _options, baseQuery) => fetchShippingSchedulePages((page) => baseQuery({
        url,
        params: { ...params, page },
        contract: SHIPPING_SCHEDULE_CONTRACT,
      })),
      providesTags: ['JobSchedule'],
      keepUnusedDataFor: 0,
    }),

    getFullSchedule: builder.query({
      query: (url) => fullScheduleRequest(url),
      providesTags: ['JobSchedule'],
      keepUnusedDataFor: 0,
    }),

    // ── Zone Count (a single job's Zones field) ─────────────────────────────
    // Replaces: fetchZones in zoneCountSlice
    getZoneCount: builder.query({
      query: (jobId) => ({ url: `/general-project-info?id=${jobId}` }),
      transformResponse: (response) => response?.[0]?.Zones ?? null,
      providesTags: (result, error, jobId) => [
        { type: 'GeneralProjectInfo', id: jobId },
      ],
    }),

    // ── Change Order Details ─────────────────────────────────────────────────
    // Replaces: fetchAllChangeOrderDetails in changeOrderDetailsSlice
    getChangeOrderDetails: builder.query({
      query: (projectId) =>
        projectId
          ? { url: `/change-order-details?projectID=${projectId}` }
          : { url: '/change-order-details' },
      providesTags: (result, error, projectId) =>
        projectId
          ? [{ type: 'ChangeOrderDetails', id: projectId }]
          : ['ChangeOrderDetails'],
    }),

    getChangeOrderParent: builder.query({
      query: (projectId) =>
        projectId
          ? { url: `/change-order-parent?projectID=${projectId}` }
          : { url: '/change-order-parent' },
      providesTags: (result, error, projectId) =>
        projectId
          ? [{ type: 'ChangeOrderParent', id: projectId }]
          : [{ type: 'ChangeOrderParent', id: 'LIST' }],
    }),

    // Replaces: postChangeOrderDetail in postChangeOrderDetailSlice
    postChangeOrderDetail: builder.mutation({
      query: (payload) => ({
        url: payload.projectID
          ? `/change-order-details?projectID=${payload.projectID}`
          : '/change-order-details',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: 'ChangeOrderDetails', id: payload.projectID },
      ],
    }),

    // Replaces: deleteChangeOrderItem in changeOrderSlice
    deleteChangeOrderItem: builder.mutation({
      query: (id) => ({
        url: '/change-order-details',
        method: 'DELETE',
        data: { ID: id },
      }),
      invalidatesTags: ['ChangeOrderDetails'],
    }),

    // ── Change Log ──────────────────────────────────────────────────────────
    // Replaces: getChangeLog in postChangeLogSlice
    getChangeLog: builder.query({
      query: (projectId) => ({
        url: `/change-log?projectID=${projectId}`,
      }),
      providesTags: (result, error, projectId) => [
        { type: 'ChangeLog', id: projectId },
      ],
    }),

    // Replaces: postChangeLog in postChangeLogSlice
    postChangeLog: builder.mutation({
      query: ({ projectID, ...rest }) => ({
        url: `/change-log?projectID=${projectID}`,
        method: 'POST',
        data: { projectID, ...rest },
      }),
      invalidatesTags: (result, error, { projectID }) => [
        { type: 'ChangeLog', id: projectID },
      ],
    }),

    // ── Sales Targets ───────────────────────────────────────────────────────
    // Replaces: fetchSalesTargetsData / createSalesTargetRecord / updateSalesTargetRecord
    getSalesTargets: builder.query({
      query: () => ({ url: '/booking-sales-target' }),
      providesTags: ['SalesTargets'],
    }),

    createSalesTarget: builder.mutation({
      query: (data) => ({
        url: '/booking-sales-target',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['SalesTargets'],
    }),

    updateSalesTarget: builder.mutation({
      query: ({ id, field, value }) => ({
        url: `/booking-sales-target?id=${id}`,
        method: 'PATCH',
        data: { [field]: value },
      }),
      invalidatesTags: ['SalesTargets'],
    }),

    // ── Pump Details ─────────────────────────────────────────────────────────
    // Replaces: fetchPumpDetails in pumpDetailsSlice
    getPumpDetails: builder.query({
      query: (projectID) =>
        projectID
          ? { url: `/pump-details?projectID=${projectID}` }
          : { url: '/pump-details' },
      providesTags: (result, error, projectID) =>
        projectID
          ? [{ type: 'PumpDetails', id: projectID }]
          : ['PumpDetails'],
    }),

    // ── SoldTo / ShipTo ──────────────────────────────────────────────────────
    // Replaces: fetchAllSellPrices / refreshSellPrices in sellPriceSlice
    getSoldToShipTo: builder.query({
      query: (projectID) =>
        projectID
          ? { url: `/soldto-shipto-info?projectID=${projectID}` }
          : { url: '/soldto-shipto-info' },
      providesTags: (result, error, projectID) =>
        projectID
          ? [{ type: 'SoldToShipTo', id: projectID }]
          : ['SoldToShipTo'],
    }),

    // ── Price Breakdown ──────────────────────────────────────────────────────
    getPriceBreakdown: builder.query({
      query: (projectID) =>
        projectID
          ? { url: `/price-breakdown-info?projectID=${projectID}` }
          : { url: '/price-breakdown-info' },
      providesTags: (result, error, projectID) =>
        projectID
          ? [{ type: 'PriceBreakdown', id: projectID }]
          : ['PriceBreakdown'],
    }),

    // ── Station Enclosure ────────────────────────────────────────────────────
    getStationEnclosure: builder.query({
      query: (projectID) => ({
        url: `/station-enclosure?projectID=${projectID}`,
      }),
      providesTags: (result, error, projectID) => [
        { type: 'StationEnclosure', id: projectID },
      ],
    }),

    // ── Fertigation ──────────────────────────────────────────────────────────
    getFertigation: builder.query({
      query: (projectID) => ({
        url: `/fertigation?projectID=${projectID}`,
      }),
      providesTags: (result, error, projectID) => [
        { type: 'Fertigation', id: projectID },
      ],
    }),

    // ── HC Requirements ──────────────────────────────────────────────────────
    getHcRequirements: builder.query({
      query: (projectID) => ({
        url: `/hc-requirements?projectID=${projectID}`,
      }),
      providesTags: (result, error, projectID) => [
        { type: 'HcRequirements', id: projectID },
      ],
    }),

    // ── Pump Manifold Piping ─────────────────────────────────────────────────
    getPumpManifoldPiping: builder.query({
      query: (projectID) => ({
        url: `/pump-manifold-piping?projectID=${projectID}`,
      }),
      providesTags: (result, error, projectID) => [
        { type: 'PumpManifoldPiping', id: projectID },
      ],
    }),

    // ── Bookings Log ─────────────────────────────────────────────────────────
    // Replaces: fetchBookingsData in bookingsLogSlice
    getBookingsGeneralProject: builder.query({
      query: () => ({ url: '/general-project-info?excludeSpecialJobs=true' }),
      providesTags: [{ type: 'BookingsLog', id: 'generalProject' }],
    }),

    getBookingJobDetail: builder.query({
      query: () => ({ url: '/booking-job-detail' }),
      providesTags: [{ type: 'BookingsLog', id: 'jobDetail' }],
    }),

    getBookingCommissionSplit: builder.query({
      query: () => ({ url: '/booking-commission-split' }),
      providesTags: [{ type: 'BookingsLog', id: 'commissionSplit' }],
    }),

    getSalesTerritory: builder.query({
      query: () => ({ url: '/sales-territory' }),
      providesTags: [{ type: 'BookingsLog', id: 'salesTerritory' }],
    }),

    // Mutations for bookingsLog
    updateBookingJobDetailRecord: builder.mutation({
      query: ({ id, data }) => ({
        url: `/booking-job-detail?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: [{ type: 'BookingsLog', id: 'jobDetail' }],
    }),

    createBookingJobDetailRecord: builder.mutation({
      query: (data) => ({
        url: '/booking-job-detail',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'BookingsLog', id: 'jobDetail' }],
    }),

    createCommissionSplitRecord: builder.mutation({
      query: (data) => ({
        url: '/booking-commission-split',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'BookingsLog', id: 'commissionSplit' }],
    }),

    updateCommissionSplitRecord: builder.mutation({
      query: ({ id, data }) => ({
        url: `/booking-commission-split?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: [{ type: 'BookingsLog', id: 'commissionSplit' }],
    }),

    deleteCommissionSplitRecord: builder.mutation({
      query: (id) => ({
        url: `/booking-commission-split?id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'BookingsLog', id: 'commissionSplit' }],
    }),

    updateGeneralProjectRecord: builder.mutation({
      query: ({ id, data }) => ({
        url: `/general-project-info?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: [
        { type: 'BookingsLog', id: 'generalProject' },
        'JobSchedule',
      ],
    }),

    updateSoldToShipToRecord: builder.mutation({
      query: ({ id, data }) => ({
        url: `/soldto-shipto-info?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['SoldToShipTo'],
    }),

    updatePriceBreakdownRecord: builder.mutation({
      query: ({ id, data }) => ({
        url: `/price-breakdown-info?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PriceBreakdown'],
    }),

    // ── Engineering Tasks ────────────────────────────────────────────────────
    getEngineeringTasks: builder.query({
      query: (params) => ({
        url: params
          ? `/engineering-tasks?${new URLSearchParams(params).toString()}`
          : '/engineering-tasks',
      }),
      providesTags: ['EngineeringTasks'],
    }),

    createEngineeringTask: builder.mutation({
      query: (data) => ({
        url: '/engineering-tasks',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['EngineeringTasks'],
    }),

    updateEngineeringTask: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/engineering-tasks?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['EngineeringTasks'],
    }),

    deleteEngineeringTask: builder.mutation({
      query: (id) => ({
        url: '/engineering-tasks',
        method: 'DELETE',
        data: { ID: id },
      }),
      invalidatesTags: ['EngineeringTasks'],
    }),

    // ── Engineering Task Comments ────────────────────────────────────────────
    getEngineeringTaskComments: builder.query({
      query: (taskId) => ({
        url: `/engineering-task-comments?taskID=${taskId}`,
      }),
      providesTags: (result, error, taskId) => [
        { type: 'EngineeringTaskComments', id: taskId },
      ],
    }),

    createEngineeringTaskComment: builder.mutation({
      query: (data) => ({
        url: '/engineering-task-comments',
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, data) => [
        { type: 'EngineeringTaskComments', id: data.TaskID },
      ],
    }),

    // ── Protected Time ───────────────────────────────────────────────────────
    getProtectedTime: builder.query({
      query: (params) => ({
        url: params
          ? `/engineering-protected-time?${new URLSearchParams(params).toString()}`
          : '/engineering-protected-time',
      }),
      providesTags: ['ProtectedTime'],
    }),

    createProtectedTime: builder.mutation({
      query: (data) => ({
        url: '/engineering-protected-time',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ProtectedTime'],
    }),

    updateProtectedTime: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/engineering-protected-time?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ProtectedTime'],
    }),

    deleteProtectedTime: builder.mutation({
      query: (id) => ({
        url: '/engineering-protected-time',
        method: 'DELETE',
        data: { ID: id },
      }),
      invalidatesTags: ['ProtectedTime'],
    }),

    // ── PTO ──────────────────────────────────────────────────────────────────
    getPto: builder.query({
      query: (params) => ({
        url: params
          ? `/engineering-pto?${new URLSearchParams(params).toString()}`
          : '/engineering-pto',
      }),
      providesTags: ['Pto'],
    }),

    createPto: builder.mutation({
      query: (data) => ({
        url: '/engineering-pto',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Pto'],
    }),

    updatePto: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/engineering-pto?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['Pto'],
    }),

    deletePto: builder.mutation({
      query: (id) => ({
        url: '/engineering-pto',
        method: 'DELETE',
        data: { ID: id },
      }),
      invalidatesTags: ['Pto'],
    }),

    // ── User Preferences ─────────────────────────────────────────────────────
    // Replaces: fetchUserPermissions / fetchPermissionTemplates in permissionsSlice
    getUserPreferences: builder.query({
      query: () => ({ url: '/user-preferences' }),
      providesTags: ['UserPreferences'],
    }),

    updateUserPreference: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/user-preferences?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    createUserPreference: builder.mutation({
      query: (data) => ({
        url: '/user-preferences',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    deleteUserPreference: builder.mutation({
      query: (id) => ({
        url: '/user-preferences',
        method: 'DELETE',
        data: { ID: id },
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    // ── Component Policies ─────────────────────────────────────────────────
    getComponentPolicies: builder.query({
      query: (filters = {}) => {
        const params = new URLSearchParams();

        if (filters.componentKey) {
          params.set('componentKey', String(filters.componentKey));
        }

        if (filters.environment) {
          params.set('environment', normalizeEnvironmentValue(filters.environment));
        }

        if (filters.status) {
          params.set('status', String(filters.status));
        }

        const query = params.toString();
        return {
          url: query ? `/global-permissions?${query}` : '/global-permissions',
        };
      },
      transformResponse: (response) => {
        const normalized = normalizeComponentPolicyResponse(response);
        return Array.isArray(normalized) ? normalized : (normalized ? [normalized] : []);
      },
      providesTags: (result) => {
        const listTag = { type: 'ComponentPolicies', id: 'LIST' };

        if (!Array.isArray(result)) {
          return [listTag];
        }

        const entityTags = result
          .map((item) => item?.id ?? item?.ID)
          .filter((id) => id !== undefined && id !== null)
          .map((id) => ({ type: 'ComponentPolicies', id: String(id) }));

        return [listTag, ...entityTags];
      },
    }),

    getComponentPolicyById: builder.query({
      query: (id) => ({
        url: `/global-permissions?id=${id}`,
      }),
      transformResponse: (response) => {
        const normalized = normalizeComponentPolicyResponse(response);
        if (Array.isArray(normalized)) {
          return normalized[0] || null;
        }

        return normalized || null;
      },
      providesTags: (result, error, id) => [
        { type: 'ComponentPolicies', id: String(id) },
      ],
    }),

    createComponentPolicy: builder.mutation({
      query: (data) => ({
        url: '/global-permissions',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'ComponentPolicies', id: 'LIST' }],
    }),

    updateComponentPolicy: builder.mutation({
      query: ({ id, ...data }) => ({
        url: '/global-permissions',
        method: 'PATCH',
          data: { id, ...data },
        contract: {
            requestCase: 'pascal',
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ComponentPolicies', id: String(id) },
        { type: 'ComponentPolicies', id: 'LIST' },
      ],
    }),

    deleteComponentPolicy: builder.mutation({
      query: (id) => ({
        url: '/global-permissions',
        method: 'DELETE',
        data: { ID: id },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ComponentPolicies', id: String(id) },
        { type: 'ComponentPolicies', id: 'LIST' },
      ],
    }),

    // ── Panel Sizing ─────────────────────────────────────────────────────────
    getPanelSizing: builder.query({
      query: (projectID) => ({
        url: `/panel-sizing?projectID=${projectID}`,
      }),
      providesTags: (result, error, projectID) => [
        { type: 'PanelSizing', id: projectID },
      ],
    }),

    createPanelSizing: builder.mutation({
      query: (data) => ({
        url: '/panel-sizing',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PanelSizing'],
    }),

    updatePanelSizing: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/panel-sizing?id=${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['PanelSizing'],
    }),

    getPumpUpdateData: builder.query({
      query: (pumpParentId) => ({
        url: `${process.env.REACT_APP_HAS_PUMP_URL || ''}?id=${pumpParentId}`,
      }),
    }),

    // ── Generic / Utility endpoints ──────────────────────────────────────────
    // Replaces: fetchData / postData / patchData / deleteData in apiCallSlice
    getData: builder.query({
      query: ({ url, params, headers, contract, requestPolicy }) => ({ url, params, headers, contract, requestPolicy }),
      // Never cache generic requests – callers expect always-fresh data,
      // and caching arbitrary URLs could serve stale results unexpectedly.
      keepUnusedDataFor: 0,
    }),

    postData: builder.mutation({
      query: ({ url, data, headers, contract, requestPolicy }) => ({ url, method: 'POST', data, headers, contract, requestPolicy }),
    }),

    putData: builder.mutation({
      query: ({ url, data, headers, contract, requestPolicy }) => ({ url, method: 'PUT', data, headers, contract, requestPolicy }),
    }),

    patchData: builder.mutation({
      async queryFn(args = {}, api, extraOptions, baseQuery) {
        const {
          url,
          data,
          baselineData = null,
          allowFullPayload = false,
          requestPolicy,
          headers,
          contract,
        } = args;

        if (!url) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              message: 'Missing url for patchData mutation.',
              endpoint: 'patchData',
            },
          };
        }

        const sanitizedPayload = sanitizePatchPayload(data);

        const canBuildDelta = !allowFullPayload && isPlainObject(sanitizedPayload);
        const resolvedBaseline = canBuildDelta
          ? (isPlainObject(baselineData)
              ? baselineData
              : derivePatchBaselineFromState({
                  state: api?.getState?.(),
                  url,
                  payload: sanitizedPayload,
                }))
          : null;

        const payloadToSend = canBuildDelta
          ? buildPatchDelta({ payload: sanitizedPayload, baseline: resolvedBaseline })
          : sanitizedPayload;

        if (isPlainObject(payloadToSend)) {
          const mutableKeys = Object.keys(payloadToSend).filter(
            (key) => !PATCH_ID_KEYS.has(key)
          );

          if (!allowFullPayload && mutableKeys.length === 0) {
            return {
              data: {
                skipped: true,
                reason: 'No changed fields detected for patch request.',
              },
            };
          }
        }

        return baseQuery(
          { url, method: 'PATCH', data: payloadToSend, headers, contract, requestPolicy },
          api,
          extraOptions
        );
      },
    }),

    deleteData: builder.mutation({
      query: ({ url, data, deleteId, headers, contract, requestPolicy }) => ({
        url,
        method: 'DELETE',
        data: data ?? deleteId,
        headers,
        contract,
        requestPolicy,
      }),
    }),
  }),
});

// ---------------------------------------------------------------------------
// Export auto-generated React hooks for use in components
// ---------------------------------------------------------------------------
export const {
  // Critical data
  useGetCriticalDataQuery,
  // Dropdown values
  useGetDropdownValuesQuery,
  useCreateDropdownValueMutation,
  useUpdateDropdownValueMutation,
  useDeleteDropdownValueMutation,
  // TBD
  useGetTbdDataQuery,
  // Full job
  useGetFullJobQuery,
  // General project info
  useGetGeneralProjectInfoQuery,
  usePatchGeneralProjectInfoMutation,
  // Job list (schedule)
  useGetJobListQuery,
  useGetFullScheduleQuery,
  useGetShippingScheduleQuery,
  // Zone count
  useGetZoneCountQuery,
  // Change order details
  useGetChangeOrderDetailsQuery,
  useGetChangeOrderParentQuery,
  usePostChangeOrderDetailMutation,
  useDeleteChangeOrderItemMutation,
  // Change log
  useGetChangeLogQuery,
  usePostChangeLogMutation,
  // Sales targets
  useGetSalesTargetsQuery,
  useCreateSalesTargetMutation,
  useUpdateSalesTargetMutation,
  // Pump details
  useGetPumpDetailsQuery,
  // SoldTo/ShipTo
  useGetSoldToShipToQuery,
  // Price breakdown
  useGetPriceBreakdownQuery,
  // Section-level queries
  useGetStationEnclosureQuery,
  useGetFertigationQuery,
  useGetHcRequirementsQuery,
  useGetPumpManifoldPipingQuery,
  // Bookings
  useGetBookingsGeneralProjectQuery,
  useGetBookingJobDetailQuery,
  useGetBookingCommissionSplitQuery,
  useGetSalesTerritoryQuery,
  useUpdateBookingJobDetailRecordMutation,
  useCreateBookingJobDetailRecordMutation,
  useCreateCommissionSplitRecordMutation,
  useUpdateCommissionSplitRecordMutation,
  useDeleteCommissionSplitRecordMutation,
  useUpdateGeneralProjectRecordMutation,
  useUpdateSoldToShipToRecordMutation,
  useUpdatePriceBreakdownRecordMutation,
  // Engineering tasks
  useGetEngineeringTasksQuery,
  useCreateEngineeringTaskMutation,
  useUpdateEngineeringTaskMutation,
  useDeleteEngineeringTaskMutation,
  // Engineering task comments
  useGetEngineeringTaskCommentsQuery,
  useCreateEngineeringTaskCommentMutation,
  // Protected time
  useGetProtectedTimeQuery,
  useCreateProtectedTimeMutation,
  useUpdateProtectedTimeMutation,
  useDeleteProtectedTimeMutation,
  // PTO
  useGetPtoQuery,
  useCreatePtoMutation,
  useUpdatePtoMutation,
  useDeletePtoMutation,
  // User preferences
  useGetUserPreferencesQuery,
  useUpdateUserPreferenceMutation,
  useCreateUserPreferenceMutation,
  useDeleteUserPreferenceMutation,
  // Component policies
  useGetComponentPoliciesQuery,
  useGetComponentPolicyByIdQuery,
  useCreateComponentPolicyMutation,
  useUpdateComponentPolicyMutation,
  useDeleteComponentPolicyMutation,
  // Panel sizing
  useGetPanelSizingQuery,
  useCreatePanelSizingMutation,
  useUpdatePanelSizingMutation,
  // Pump update ping
  useGetPumpUpdateDataQuery,
  // Generic utilities
  useGetDataQuery,
  usePostDataMutation,
  usePutDataMutation,
  usePatchDataMutation,
  useDeleteDataMutation,
} = portalApi;

export const usePortalApiRequest = () => {
  const dispatch = useDispatch();

  return useCallback(async ({ method = 'GET', url, data, params, headers, contract, requestPolicy }) => {
    const methodUpper = String(method || 'GET').toUpperCase();

    if (methodUpper === 'GET') {
      const request = dispatch(portalApi.endpoints.getData.initiate(
        { url, params, headers, contract },
        { subscribe: false, forceRefetch: true }
      ));

      try {
        return await request.unwrap();
      } finally {
        request.unsubscribe?.();
      }
    }

    const endpointByMethod = {
      POST: 'postData',
      PUT: 'putData',
      PATCH: 'patchData',
      DELETE: 'deleteData',
    };
    const endpointName = endpointByMethod[methodUpper];

    if (!endpointName) {
      throw new Error(`Unsupported portalApi request method: ${methodUpper}`);
    }

    const args = {
      url,
      data,
      headers,
      contract,
      requestPolicy,
      ...(methodUpper === 'PATCH' ? { allowFullPayload: true } : {}),
    };

    return dispatch(portalApi.endpoints[endpointName].initiate(args)).unwrap();
  }, [dispatch]);
};

export default portalApi;
